/**
 * Safegear Product Add-ons - Horizon 4.1.3
 *
 * Rewritten against Horizon 4.1.3 / Shopify Standard Events. Replaces the
 * 2.1.6-era implementation, which relied on DOM and event contracts that no
 * longer exist.
 *
 * Architecture notes:
 *
 * - QUANTITY. The vendor `quantity-selector` snippet now renders a
 *   <quantity-selector-component> custom element which dispatches
 *   QuantitySelectorUpdateEvent ('quantity-selector:update') from the HOST
 *   ELEMENT, not from the <input>. The old listener tested
 *   `e.target instanceof HTMLInputElement` and therefore returned on every
 *   event - addon quantity changes never registered. We resolve the card via
 *   `e.target.closest('.addon-card')` instead.
 *
 * - CART WRITES. Add-ons are attached to the main product as NESTED CART LINES
 *   using `parent_id`, which only the AJAX Cart API supports - the Standard
 *   Actions `CartLineInput` has no parent field. So the write stays a raw POST
 *   to Theme.routes.cart_add_url. To keep Horizon's UI in sync we wrap that
 *   POST in a CartLinesUpdateEvent promise, which is exactly what the cart
 *   drawer and header already listen for. That removes the old file's manual
 *   cart-badge writes, subtotal writes, section re-rendering and
 *   `outerHTML =` replacement, all of which fight 4.1.3's morph-based updates.
 *
 * - MONEY. Uses @theme/money-formatting, which replicates the Liquid `money`
 *   filter including European separators. Intl.NumberFormat does not, and
 *   would disagree with server-rendered prices on the AT and LU markets.
 *
 * - VAT. `data-vat-rate` comes from snippets/custom.vat-rate.liquid, the same
 *   source snippets/custom.price-vat.liquid uses. Net is derived identically:
 *   round(gross / (1 + rate)). Server and client cannot drift.
 *
 * - STICKY. Positioning is CSS (`position: sticky` against --header-height),
 *   not a MutationObserver on document.body.
 */

import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

const SELECTORS = {
  root: '[data-addons-root]',
  card: '.addon-card',
  checkbox: '.addon-checkbox',
  checkboxWrapper: '.addon-checkbox-wrapper',
  quantityComponent: 'quantity-selector-component',
  addonsList: '#cart-addons-list',
  totalGross: '#cart-total',
  totalNet: '#cart-total-net',
  addButton: '#main-add-to-cart',
  clearButton: '#clear-addons',
  loading: '#cart-loading',
  configInput: '#selected_addons_config',
};

class ProductAddons {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;

    const data = root.dataset;
    this.moneyFormat = data.moneyFormat || '{{amount}}';
    this.currency = data.shopCurrency || 'EUR';
    this.vatRate = parseFloat(data.vatRate) || 0;
    this.vatKnown = data.vatKnown === 'true' && this.vatRate > 0;
    this.labelExcl = data.vatLabelExcl || '';
    this.labelIncl = data.vatLabelIncl || '';
    this.removeText = data.removeButtonText || 'Remove';

    this.mainVariantId = data.selectedVariantId || null;
    this.mainPrice = parseInt(data.selectedVariantPrice, 10) || 0;
    this.inCart = false;

    /** @type {Map<string, {variantId: string, title: string, price: number, quantity: number, image: string|null}>} */
    this.addons = new Map();

    this.els = {
      addonsList: root.querySelector(SELECTORS.addonsList),
      totalGross: root.querySelector(SELECTORS.totalGross),
      totalNet: root.querySelector(SELECTORS.totalNet),
      addButton: root.querySelector(SELECTORS.addButton),
      clearButton: root.querySelector(SELECTORS.clearButton),
      loading: root.querySelector(SELECTORS.loading),
      configInput: root.querySelector(SELECTORS.configInput),
    };

    this.bindEvents();
    this.render();
  }

  // --- money and VAT -------------------------------------------------------

  /** @param {number} cents */
  money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  /**
   * Net (VAT-exclusive) value of a gross amount. Mirrors the Liquid maths in
   * custom.price-vat.liquid exactly: gross / (1 + rate), rounded to minor units.
   * @param {number} gross
   */
  net(gross) {
    if (!this.vatKnown) return gross;
    return Math.round(gross / (1 + this.vatRate));
  }

  // --- events --------------------------------------------------------------

  bindEvents() {
    // Add-on selection.
    this.root.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.matches(SELECTORS.checkbox)) return;
      this.toggleAddon(target);
    });

    // Quantity. Dispatched from <quantity-selector-component>, so resolve the
    // card from the event target rather than assuming an <input>.
    this.root.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const card = target.closest(SELECTORS.card);
      if (!card || card.dataset.addonType === 'yes_no') return;

      const quantity = Number(event.detail?.quantity);
      if (!Number.isFinite(quantity) || quantity < 1) return;

      const entry = this.addons.get(card.dataset.variantId);
      if (!entry) return;

      entry.quantity = quantity;
      this.render();
    });

    this.els.addButton?.addEventListener('click', () => {
      if (this.inCart) {
        window.location.href = '/checkout';
      } else {
        this.addToCart();
      }
    });

    this.els.clearButton?.addEventListener('click', () => this.clear());

    // Remove buttons are delegated, so the list can re-render freely.
    this.els.addonsList?.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-remove-variant]') : null;
      if (!button) return;
      this.deselect(button.getAttribute('data-remove-variant'));
    });

    // Main variant changes. 4.1.3 dispatches the Shopify standard product
    // select event; its promise resolves with the newly selected variant.
    document.addEventListener(StandardEvents.productSelect, (event) => {
      event.promise
        ?.then(({ variant }) => {
          if (!variant?.id) return;
          this.mainVariantId = String(variant.id).replace(/^gid:\/\/shopify\/ProductVariant\//, '');

          const amount = variant.price?.amount;
          const minor = amount == null ? null : convertMoneyToMinorUnits(String(amount), this.currency);
          if (minor != null) this.mainPrice = minor;

          // A variant change means the previous add is no longer the thing in
          // the cart, so revert the button to its "add" state.
          this.inCart = false;
          this.render();
        })
        .catch(() => {});
    });
  }

  // --- selection state -----------------------------------------------------

  /** @param {HTMLInputElement} checkbox */
  toggleAddon(checkbox) {
    const card = checkbox.closest(SELECTORS.card);
    const wrapper = checkbox.closest(SELECTORS.checkboxWrapper);
    if (!card || !wrapper) return;

    const variantId = card.dataset.variantId;
    if (!variantId) return;

    if (!checkbox.checked) {
      this.deselect(variantId);
      return;
    }

    const component = card.querySelector(SELECTORS.quantityComponent);
    const quantity = component ? parseInt(component.querySelector('input')?.value, 10) || 1 : 1;

    this.addons.set(variantId, {
      variantId,
      title: wrapper.dataset.productTitle || card.querySelector('.addon-title')?.textContent?.trim() || '',
      price: parseInt(card.dataset.price, 10) || 0,
      quantity: card.dataset.addonType === 'yes_no' ? 1 : quantity,
      image: card.querySelector('.addon-image img')?.getAttribute('src') || null,
    });

    card.classList.add('added');
    wrapper.classList.add('checked');
    this.render();
  }

  /** @param {string|null} variantId */
  deselect(variantId) {
    if (!variantId || !this.addons.has(variantId)) return;
    this.addons.delete(variantId);

    const card = this.root.querySelector(`${SELECTORS.card}[data-variant-id="${CSS.escape(variantId)}"]`);
    if (card) {
      card.classList.remove('added');
      const wrapper = card.querySelector(SELECTORS.checkboxWrapper);
      wrapper?.classList.remove('checked');
      const checkbox = card.querySelector(SELECTORS.checkbox);
      if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
    }

    this.render();
  }

  clear() {
    for (const variantId of [...this.addons.keys()]) this.deselect(variantId);
  }

  // --- rendering -----------------------------------------------------------

  get total() {
    let total = this.mainPrice;
    for (const addon of this.addons.values()) total += addon.price * addon.quantity;
    return total;
  }

  render() {
    this.renderTotals();
    this.renderList();
    this.renderButtons();
    this.renderConfig();
  }

  renderTotals() {
    const gross = this.total;
    if (this.els.totalGross) this.els.totalGross.textContent = this.money(gross);
    if (this.els.totalNet) this.els.totalNet.textContent = this.money(this.net(gross));
  }

  renderList() {
    const list = this.els.addonsList;
    if (!list) return;

    list.textContent = '';

    for (const addon of this.addons.values()) {
      const row = document.createElement('div');
      row.className = 'cart-addon-item';
      row.dataset.variantId = addon.variantId;

      const image = document.createElement('div');
      image.className = 'cart-addon-image';
      if (addon.image) {
        const img = document.createElement('img');
        img.src = addon.image;
        img.alt = '';
        img.loading = 'lazy';
        image.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'cart-addon-info';

      const title = document.createElement('div');
      title.className = 'cart-addon-title';
      // textContent, not innerHTML - product titles are merchant data.
      title.textContent = addon.quantity > 1 ? `${addon.title} (x${addon.quantity})` : addon.title;

      const price = document.createElement('div');
      price.className = 'cart-addon-price';
      price.textContent = this.money(addon.price * addon.quantity);

      info.append(title, price);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cart-addon-remove';
      remove.setAttribute('data-remove-variant', addon.variantId);
      remove.textContent = this.removeText;

      row.append(image, info, remove);
      list.appendChild(row);
    }
  }

  renderButtons() {
    const { addButton, clearButton } = this.els;

    if (clearButton) clearButton.hidden = this.addons.size === 0;

    if (addButton) {
      addButton.dataset.variantId = this.mainVariantId || '';
      addButton.classList.toggle('in-cart', this.inCart);
      const add = addButton.querySelector('.btn-text-add');
      const checkout = addButton.querySelector('.btn-text-checkout');
      if (add instanceof HTMLElement) add.hidden = this.inCart;
      if (checkout instanceof HTMLElement) checkout.hidden = !this.inCart;
    }
  }

  /**
   * Human-readable summary written to a line item property on the main product,
   * so the add-on selection is legible on the order without joining lines.
   */
  renderConfig() {
    const input = this.els.configInput;
    if (!(input instanceof HTMLInputElement)) return;

    if (this.addons.size === 0) {
      input.value = '';
      return;
    }

    const parts = [...this.addons.values()].map((addon) => {
      const price = this.money(addon.price * addon.quantity);
      return addon.quantity > 1 ? `${addon.title} (x${addon.quantity}) - ${price}` : `${addon.title} - ${price}`;
    });

    input.value = parts.join(', ');
  }

  // --- cart ----------------------------------------------------------------

  /** @param {boolean} state */
  setLoading(state) {
    if (this.els.loading instanceof HTMLElement) this.els.loading.hidden = !state;
    if (this.els.addButton instanceof HTMLButtonElement) this.els.addButton.disabled = state;
  }

  async addToCart() {
    if (!this.mainVariantId) return;

    this.setLoading(true);
    this.renderConfig();

    // Main line first; add-ons reference it via parent_id in the same request.
    const items = [
      {
        id: this.mainVariantId,
        quantity: 1,
        properties: { 'Additional Items': this.els.configInput?.value || '' },
      },
      ...[...this.addons.values()].map((addon) => ({
        id: addon.variantId,
        quantity: addon.quantity,
        parent_id: this.mainVariantId,
      })),
    ];

    // Announce the write to Horizon before performing it, then settle the
    // promise with the result. This is what drives the cart drawer auto-open
    // and the header count - no manual DOM updates required.
    const { promise, resolve, reject } = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });

      this.inCart = true;
      this.renderButtons();
    } catch (error) {
      resolve({ cart: null, userErrors: [{ message: error.message }], detail: { didError: true } });
      this.root.dispatchEvent(
        new CustomEvent('addons:error', { bubbles: true, detail: { message: error.message } })
      );
    } finally {
      this.setLoading(false);
    }
  }
}

const root = document.querySelector(SELECTORS.root);
if (root instanceof HTMLElement) new ProductAddons(root);
